import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { AVATARS, type GuestMe, type JoinInfo, type PublicGuest } from '@karozik/shared';
import { api, Artwork, Avatar, cn, Equalizer, Spinner, useParty } from '@karozik/ui';
import { AnimatorPanel } from '../components/AnimatorPanel';
import { EmojiPicker } from '../components/JoinForm';
import { Party } from '../components/Party';
import { Centered } from './GuestEntry';

interface KioskInfo extends JoinInfo {
  joinToken: string;
  animatorEnabled: boolean;
  guests: PublicGuest[];
}

type Screen = 'idle' | 'identify' | 'new' | 'session' | 'pin' | 'animator';

const SESSION_IDLE_MS = 45_000;
const ANIMATOR_IDLE_MS = 3 * 60_000;

/**
 * /borne/:token — the shared tablet on the lectern, for guests without a phone.
 * Sessions live in memory only and end after inactivity; nothing is stored on
 * the device.
 */
export function Kiosk() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<KioskInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>('idle');
  const [session, setSession] = useState<{ guestToken: string; me: GuestMe } | null>(null);
  const [animatorToken, setAnimatorToken] = useState<string | null>(null);
  const lastActivity = useRef(Date.now());

  const refresh = useCallback(() => {
    api<KioskInfo>(`/api/kiosk/${token}`)
      .then(setInfo)
      .catch((e: Error) => setError(e.message));
  }, [token]);
  useEffect(refresh, [refresh]);

  const auth = useMemo(
    () => ({
      role: 'kiosk' as const,
      kioskToken: token,
      ...(session ? { guestToken: session.guestToken } : {}),
      ...(animatorToken && screen === 'animator' ? { animatorToken } : {}),
    }),
    [token, session, animatorToken, screen],
  );
  const conn = useParty(info ? auth : null);

  const reset = useCallback(() => {
    setSession(null);
    setScreen('idle');
  }, []);

  // Inactivity: back to the attract screen.
  useEffect(() => {
    const touch = () => (lastActivity.current = Date.now());
    window.addEventListener('pointerdown', touch);
    window.addEventListener('keydown', touch);
    const id = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (screen === 'animator' ? idle > ANIMATOR_IDLE_MS : screen !== 'idle' && idle > SESSION_IDLE_MS) reset();
    }, 1000);
    return () => {
      window.removeEventListener('pointerdown', touch);
      window.removeEventListener('keydown', touch);
      clearInterval(id);
    };
  }, [screen, reset]);

  if (error) return <Centered emoji="🔒" title="Borne indisponible" text={error} />;
  if (!info) return <Centered emoji={<Spinner className="h-8 w-8" />} title="" text="" />;

  const startSession = async (body: { guestId: string } | { pseudo: string; emoji: string }) => {
    const s = await api<{ guestToken: string; me: GuestMe }>(`/api/kiosk/${token}/session`, { method: 'POST', json: body });
    setSession(s);
    setScreen('session');
    refresh();
  };

  if (screen === 'session' && session) {
    return (
      <Party
        conn={conn}
        me={session.me}
        variant="kiosk"
        search={{ kioskGuestToken: session.guestToken }}
        header={
          <header className="flex items-center gap-4">
            <Avatar guest={session.me} size="lg" />
            <div className="flex-1">
              <div className="text-ink-dim">Salut</div>
              <div className="font-display text-2xl font-bold">{session.me.pseudo}</div>
            </div>
            <button onClick={reset} className="rounded-2xl bg-surface-2 px-6 py-4 text-lg font-semibold">
              Terminer ✓
            </button>
          </header>
        }
      />
    );
  }

  if (screen === 'identify') return <Identify guests={info.guests} onPick={(g) => void startSession({ guestId: g.id })} onNew={() => setScreen('new')} onBack={reset} />;
  if (screen === 'new') return <NewGuest onBack={() => setScreen('identify')} onCreate={(pseudo, emoji) => startSession({ pseudo, emoji })} />;
  if (screen === 'pin') {
    return (
      <PinPad
        onBack={reset}
        onSubmit={async (pin) => {
          const r = await api<{ animatorToken: string }>(`/api/kiosk/${token}/animator`, { method: 'POST', json: { pin } });
          setAnimatorToken(r.animatorToken);
          setScreen('animator');
        }}
      />
    );
  }
  if (screen === 'animator') return <AnimatorPanel conn={conn} onExit={reset} />;

  // Attract screen.
  const state = conn.state;
  const np = state?.nowPlaying;
  return (
    <button onClick={() => setScreen('identify')} className="relative flex min-h-dvh w-full flex-col items-center justify-center gap-8 overflow-hidden px-10 text-center">
      {np?.track.artworkUrl && <img src={np.track.artworkUrl} alt="" className="absolute inset-0 h-full w-full scale-125 object-cover opacity-25 blur-3xl" />}
      <div className="relative flex flex-col items-center">
        <div className="text-lg font-semibold tracking-widest text-pink uppercase">{info.event.name}</div>
        {np ? (
          <div className="mt-6 flex items-center gap-6 text-left">
            <Artwork track={np.track} className="h-44 w-44" />
            <div>
              <div className="flex items-center gap-2 text-pink">
                <Equalizer /> En ce moment
              </div>
              <div className="font-display mt-2 text-4xl font-bold">{np.track.title}</div>
              <div className="text-2xl text-ink-dim">{np.track.artist}</div>
            </div>
          </div>
        ) : (
          <div className="mt-6 text-2xl text-ink-dim">La musique arrive…</div>
        )}
      </div>
      <div className="bg-gradient-neon animate-pulse-soft relative rounded-3xl px-12 py-8 text-3xl font-bold shadow-2xl shadow-pink/30">
        👆 Touchez l’écran pour choisir la musique
      </div>
      <p className="relative text-lg text-ink-dim">Pas de téléphone ? Pas de souci, tout se fait ici.</p>
      {info.animatorEnabled && (
        <span
          role="button"
          tabIndex={0}
          aria-label="Mode animateur"
          onClick={(e) => {
            e.stopPropagation();
            setScreen('pin');
          }}
          className="absolute right-4 bottom-4 rounded-full bg-surface/60 p-3 text-xl opacity-40"
        >
          🔒
        </span>
      )}
    </button>
  );
}

function Identify({ guests, onPick, onNew, onBack }: { guests: PublicGuest[]; onPick(g: PublicGuest): void; onNew(): void; onBack(): void }) {
  const [filter, setFilter] = useState('');
  const shown = guests.filter((g) => g.pseudo.toLowerCase().includes(filter.toLowerCase()));
  return (
    <main className="mx-auto flex min-h-dvh max-w-4xl flex-col px-8 pt-8 pb-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl font-bold">Qui es-tu ?</h1>
        <button onClick={onBack} className="rounded-2xl bg-surface-2 px-6 py-4 text-lg">
          Retour
        </button>
      </div>
      <button onClick={onNew} className="bg-gradient-neon mt-6 rounded-3xl py-6 text-2xl font-bold">
        ✨ Je suis nouveau / nouvelle
      </button>
      {guests.length > 8 && (
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Chercher mon pseudo…"
          className="mt-6 rounded-2xl border border-line bg-surface px-5 py-4 text-xl outline-none focus:border-pink"
        />
      )}
      <div className="mt-6 grid flex-1 auto-rows-min grid-cols-3 gap-3 overflow-y-auto">
        {shown.map((g) => (
          <button key={g.id} onClick={() => onPick(g)} className="flex items-center gap-3 rounded-2xl bg-surface p-4 text-left text-xl font-semibold active:scale-95">
            <Avatar guest={g} size="md" />
            <span className="truncate">{g.pseudo}</span>
          </button>
        ))}
      </div>
    </main>
  );
}

function NewGuest({ onBack, onCreate }: { onBack(): void; onCreate(pseudo: string, emoji: string): Promise<void> }) {
  const [pseudo, setPseudo] = useState('');
  const [emoji, setEmoji] = useState<string>(AVATARS[0]);
  const [error, setError] = useState<string | null>(null);
  return (
    <main className="mx-auto flex min-h-dvh max-w-3xl flex-col gap-6 px-8 pt-8 pb-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl font-bold">Ton pseudo</h1>
        <button onClick={onBack} className="rounded-2xl bg-surface-2 px-6 py-4 text-lg">
          Retour
        </button>
      </div>
      <input
        autoFocus
        value={pseudo}
        onChange={(e) => setPseudo(e.target.value)}
        maxLength={20}
        placeholder="Tonton Jacques…"
        className="rounded-2xl border border-line bg-surface px-6 py-5 text-3xl outline-none focus:border-pink"
      />
      <EmojiPicker value={emoji} onChange={setEmoji} large />
      {error && <p className="rounded-xl bg-danger/15 px-4 py-3 text-lg text-danger">{error}</p>}
      <button
        disabled={pseudo.trim().length < 2}
        onClick={() => onCreate(pseudo, emoji).catch((e: Error) => setError(e.message))}
        className="bg-gradient-neon mt-auto rounded-3xl py-6 text-2xl font-bold disabled:opacity-40"
      >
        C’est parti {emoji}
      </button>
    </main>
  );
}

function PinPad({ onBack, onSubmit }: { onBack(): void; onSubmit(pin: string): Promise<void> }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const press = (d: string) => {
    setError(false);
    setPin((p) => (p + d).slice(0, 8));
  };
  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col items-center justify-center gap-6 px-8">
      <h1 className="font-display text-3xl font-bold">Mode animateur</h1>
      <div className={cn('flex h-16 items-center gap-3 text-4xl', error && 'text-danger')}>{pin ? '•'.repeat(pin.length) : <span className="text-ink-faint">Code</span>}</div>
      <div className="grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '⌫', '0', 'OK'].map((k) => (
          <button
            key={k}
            onClick={() => {
              if (k === '⌫') setPin((p) => p.slice(0, -1));
              else if (k === 'OK') onSubmit(pin).catch(() => (setError(true), setPin('')));
              else press(k);
            }}
            className={cn('h-20 w-20 rounded-2xl text-2xl font-bold', k === 'OK' ? 'bg-gradient-neon' : 'bg-surface-2')}
          >
            {k}
          </button>
        ))}
      </div>
      <button onClick={onBack} className="text-ink-dim">
        Annuler
      </button>
    </main>
  );
}
